/**
 * Permission Directives
 * ─────────────────────
 * v-permission="'projects.create'"          — removes element if user lacks the permission
 * v-permission="['projects.create', 'projects.manage']" — requires ANY of the listed permissions
 * v-permission.all="['a', 'b']"             — requires ALL permissions
 * v-permission.hide="'projects.create'"     — hides (visibility:hidden) instead of removing
 *
 * v-role="'PMO Manager'"                    — removes element if user doesn't have that role
 * v-role="['PMO Manager', 'Admin']"         — removes element if user has none of those roles
 *
 * Permission directives respect the administrative bypass; role directives
 * always require an exact assigned role.
 *
 * Registration (main.js):
 *   import { permissionDirective, roleDirective } from '@/directives/permission'
 *   app.directive('permission', permissionDirective)
 *   app.directive('role', roleDirective)
 */

import { useAuthStore } from '@/stores/auth'

// ── Helpers ────────────────────────────────────────────────────────────────

function getStore() {
  // useAuthStore() is safe to call outside setup() as long as Pinia is active.
  return useAuthStore()
}

function isAdministrativeUser(store) {
  return store.hasAnyRole(['Super Admin', 'Admin'])
}

function checkPermission(store, binding) {
  if (isAdministrativeUser(store)) return true

  const permissions = Array.isArray(binding.value)
    ? binding.value
    : [binding.value]

  if (binding.modifiers?.all) {
    return store.hasAllPermissions(permissions)
  }
  return store.hasAnyPermission(permissions)
}

function applyVisibility(el, allowed, binding) {
  if (allowed) {
    // Restore element
    if (binding.modifiers?.hide) {
      el.style.visibility = ''
    } else if (el.__permParent && el.__permPlaceholder) {
      el.__permParent.insertBefore(el, el.__permPlaceholder)
      el.__permParent.removeChild(el.__permPlaceholder)
      el.__permParent = null
      el.__permPlaceholder = null
    }
  } else {
    if (binding.modifiers?.hide) {
      // Just hide — keeps layout space
      el.style.visibility = 'hidden'
    } else {
      // Remove from DOM but keep a comment placeholder so we can re-insert
      if (!el.__permPlaceholder) {
        const placeholder = document.createComment('v-permission')
        el.__permPlaceholder = placeholder
        el.__permParent = el.parentNode
        if (el.parentNode) {
          el.parentNode.insertBefore(placeholder, el)
          el.parentNode.removeChild(el)
        }
      }
    }
  }
}

// ── v-permission ───────────────────────────────────────────────────────────

export const permissionDirective = {
  mounted(el, binding) {
    const store = getStore()
    const allowed = checkPermission(store, binding)
    applyVisibility(el, allowed, binding)
  },
  updated(el, binding) {
    const store = getStore()
    const allowed = checkPermission(store, binding)
    applyVisibility(el, allowed, binding)
  },
}

// ── v-role ─────────────────────────────────────────────────────────────────

export const roleDirective = {
  mounted(el, binding) {
    const store = getStore()
    const roles = Array.isArray(binding.value) ? binding.value : [binding.value]
    const allowed = store.hasAnyRole(roles)

    if (!allowed) {
      if (!el.__roleParent) {
        const placeholder = document.createComment('v-role')
        el.__rolePlaceholder = placeholder
        el.__roleParent = el.parentNode
        if (el.parentNode) {
          el.parentNode.insertBefore(placeholder, el)
          el.parentNode.removeChild(el)
        }
      }
    }
  },
  updated(el, binding) {
    const store = getStore()
    const roles = Array.isArray(binding.value) ? binding.value : [binding.value]
    const allowed = store.hasAnyRole(roles)

    if (allowed) {
      if (el.__roleParent && el.__rolePlaceholder) {
        el.__roleParent.insertBefore(el, el.__rolePlaceholder)
        el.__roleParent.removeChild(el.__rolePlaceholder)
        el.__roleParent = null
        el.__rolePlaceholder = null
      }
    } else {
      if (!el.__rolePlaceholder) {
        const placeholder = document.createComment('v-role')
        el.__rolePlaceholder = placeholder
        el.__roleParent = el.parentNode
        if (el.parentNode) {
          el.parentNode.insertBefore(placeholder, el)
          el.parentNode.removeChild(el)
        }
      }
    }
  },
}
